import { useState } from "react";
import type { ConcentrationData, ProtocolFilter } from "../types";
import { InfoTip } from "./InfoTip";

interface ConcentrationTableProps {
  concentration: ConcentrationData;
  protocolFilter: ProtocolFilter;
}

export function ConcentrationTable({ concentration }: ConcentrationTableProps) {
  const [showAll, setShowAll] = useState(false);
  const allAssets = Object.entries(concentration.by_asset)
    .filter(([, a]) => a.total_collateral_usd >= 1_000_000)
    .sort(([, a], [, b]) => b.total_collateral_usd - a.total_collateral_usd);
  const assets = showAll ? allAssets : allAssets.slice(0, 12);

  const overlap = concentration.cross_protocol_overlap;

  return (
    <div className="card">
      <h2 className="text-base font-semibold mb-1">Concentration Risk</h2>
      <p className="text-xs text-text-muted mb-4">
        HHI and top-10 share by collateral asset
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-text-muted font-medium">
              <th className="text-left py-2">Asset</th>
              <th className="text-right py-2">Collateral</th>
              <th className="text-right py-2">Positions</th>
              <th className="text-right py-2">
                <InfoTip position="below" text="Herfindahl–Hirschman Index — sum of squared market shares. Ranges from ~0 (distributed) to 1.0 (single entity). Above 0.25 is highly concentrated; a single whale liquidation could move the market.">
                  HHI
                </InfoTip>
              </th>
              <th className="text-right py-2">
                <InfoTip position="below" text="Fraction of total collateral for this asset held by the 10 largest positions. High top-10 share means a few wallets dominate the market.">
                  Top-10 Share
                </InfoTip>
              </th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {assets.map(([symbol, data]) => (
              <tr key={symbol} className="border-t border-surface-600">
                <td className="py-2 font-sans font-medium text-text-primary">{symbol}</td>
                <td className="text-right py-2">{formatUsd(data.total_collateral_usd)}</td>
                <td className="text-right py-2 text-text-secondary">{data.position_count}</td>
                <td className="text-right py-2">
                  <span className={hhiColor(data.hhi)}>{data.hhi.toFixed(4)}</span>
                </td>
                <td className="text-right py-2">
                  <span className={shareColor(data.top_n_share)}>
                    {(data.top_n_share * 100).toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {allAssets.length > 12 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full py-2 text-xs text-accent hover:text-accent-dim transition-colors"
        >
          {showAll ? "Show less" : `Show all ${allAssets.length} assets`}
        </button>
      )}

      {/* Cross-protocol overlap summary */}
      {overlap.multi_protocol_wallets > 0 && (
        <div className="mt-4 pt-3 border-t border-surface-600">
          <p className="text-xs text-text-muted">
            <span className="font-semibold text-text-secondary">
              {overlap.multi_protocol_wallets}
            </span>{" "}
            wallets active on multiple protocols ({formatUsd(overlap.multi_protocol_collateral_usd)} collateral)
          </p>
        </div>
      )}
    </div>
  );
}

function formatUsd(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function hhiColor(hhi: number): string {
  if (hhi > 0.25) return "text-risk-danger";
  if (hhi > 0.15) return "text-risk-warning";
  if (hhi > 0.05) return "text-risk-moderate";
  return "text-risk-safe";
}

function shareColor(share: number): string {
  if (share > 0.50) return "text-risk-danger";
  if (share > 0.30) return "text-risk-warning";
  return "text-text-secondary";
}
